/**
 * useOnboarding Hook
 * Manages onboarding state for new organizers
 * PRD: PRD-onboarding-organisateur.md
 */

import { useState, useCallback, useMemo } from 'react';
import { supabaseOrganizerService } from '../supabase/organizer.service';
import { OrganizerProfile } from '../types/organizer.types';

export type OnboardingStep = 'welcome' | 'profile' | 'ready';

export interface OnboardingState {
  needsOnboarding: boolean;
  currentStep: OnboardingStep;
  isComplete: boolean;
  profileProgress: number;
  isLoading: boolean;
  error: string | null;
}

export interface OnboardingActions {
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: OnboardingStep) => void;
  completeOnboarding: () => Promise<void>;
  skipToEnd: () => void;
}

const STEPS_ORDER: OnboardingStep[] = ['welcome', 'profile', 'ready'];

/**
 * Turn a raw error (often a technical/English network message like
 * "Failed to fetch") into a readable, user-facing message.
 * PRD-robustesse-organisateur-reseau-faible.md (item 6)
 */
export function toReadableErrorMessage(err: unknown): string {
  const raw = err instanceof Error ? err.message : typeof err === 'string' ? err : '';

  if (/failed to fetch|networkerror|load failed|err_internet_disconnected|err_network|err_connection/i.test(raw)) {
    return 'Connection issue — please check your network and try again.';
  }
  if (/abort|timeout|timed out/i.test(raw)) {
    return 'This is taking longer than expected. Please check your connection and try again.';
  }

  return raw || 'Something went wrong. Please try again.';
}

/**
 * Calculate profile completion percentage
 */
function calculateProfileProgress(profile: OrganizerProfile | null): number {
  if (!profile) return 0;

  const requiredFields = [
    { field: 'organizer', weight: 30 },
    { field: 'organizerImage', weight: 30 },
    { field: 'location', weight: 20 },
    { field: 'latitude', weight: 10 },
    { field: 'longitude', weight: 10 },
  ];

  let progress = 0;

  for (const { field, weight } of requiredFields) {
    const value = profile[field as keyof OrganizerProfile];
    if (value !== undefined && value !== null && value !== '') {
      progress += weight;
    }
  }

  return Math.min(100, progress);
}

/**
 * Check if profile has minimum required fields
 */
export function isProfileComplete(profile: OrganizerProfile | null): boolean {
  if (!profile) return false;

  return Boolean(
    profile.organizer &&
    profile.organizer.trim() !== '' &&
    profile.organizerImage &&
    profile.location &&
    profile.latitude &&
    profile.longitude
  );
}

/**
 * Main hook for onboarding flow
 */
export function useOnboarding(
  organizerProfile: OrganizerProfile | null,
  userId: string | null
): OnboardingState & OnboardingActions {
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('welcome');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localComplete, setLocalComplete] = useState(false);

  // Calculate if onboarding is needed
  const needsOnboarding = useMemo(() => {
    // If locally marked as complete (just finished), don't show
    if (localComplete) return false;

    // If no profile exists yet, needs onboarding
    if (!organizerProfile) return false; // Wait for profile to load

    // If explicitly completed, don't show
    if (organizerProfile.onboardingCompleted === true) return false;

    // If onboardingCompleted is false or undefined, check profile
    return !isProfileComplete(organizerProfile) || organizerProfile.onboardingCompleted === false;
  }, [organizerProfile, localComplete]);

  // Calculate profile progress
  const profileProgress = useMemo(
    () => calculateProfileProgress(organizerProfile),
    [organizerProfile]
  );

  // Check if fully complete
  const isComplete = useMemo(() => {
    return organizerProfile?.onboardingCompleted === true || localComplete;
  }, [organizerProfile, localComplete]);

  // Navigation
  const nextStep = useCallback(() => {
    const currentIndex = STEPS_ORDER.indexOf(currentStep);
    if (currentIndex < STEPS_ORDER.length - 1) {
      setCurrentStep(STEPS_ORDER[currentIndex + 1]);
    }
  }, [currentStep]);

  const prevStep = useCallback(() => {
    const currentIndex = STEPS_ORDER.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(STEPS_ORDER[currentIndex - 1]);
    }
  }, [currentStep]);

  const goToStep = useCallback((step: OnboardingStep) => {
    if (STEPS_ORDER.includes(step)) {
      setCurrentStep(step);
    }
  }, []);

  // Skip to end (only available if profile is complete)
  const skipToEnd = useCallback(() => {
    if (isProfileComplete(organizerProfile)) {
      setCurrentStep('ready');
    }
  }, [organizerProfile]);

  // Complete onboarding
  const completeOnboarding = useCallback(async () => {
    if (!userId) {
      setError('User not authenticated');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await supabaseOrganizerService.completeOnboarding(userId);
      setLocalComplete(true);
      console.log('✅ Onboarding completed successfully');
    } catch (err) {
      console.error('❌ Error completing onboarding:', err);
      const message = toReadableErrorMessage(err);
      setError(message);
      // Re-throw so callers (OnboardingModal) know the completion actually
      // failed and must NOT navigate away as if it succeeded.
      throw err instanceof Error ? err : new Error(message);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  return {
    // State
    needsOnboarding,
    currentStep,
    isComplete,
    profileProgress,
    isLoading,
    error,
    // Actions
    nextStep,
    prevStep,
    goToStep,
    completeOnboarding,
    skipToEnd,
  };
}

export default useOnboarding;
